<template>
    <ResizableContent
        class="user-info-widget"
        :class="{ minimized }"
        :handles="['ml']"
        :on-resize="onResize"
        :max-width="maxUserInfoWidgetWidth"
        :min-width="minUserInfoWidgetWidth"
        :width="cloneWidth"
        :disabled-resize="disabledResize"
    >
        <div class="collapse-widget" @click="maximize" v-if="!disabledResize">
            <div class="material-icons">chevron_left</div>
        </div>
        <div class="user-info-head">
            <div class="user-info-details user-info-widget-title">{{ getTabTitle() }}</div>
            <div class="material-icons" @click="maximize" v-if="minimized">add</div>
            <div class="material-icons" @click="minimize" v-else-if="!disabledResize">close</div>
        </div>
        <div class="user-info-wrapper">
            <div class="user-info-content">
                <ContactDetail :contact="contact" v-if="tab === 'user-info'" />
                <MetaReferrals :contact="contact" v-if="tab === 'meta-referrals'" />
                <MessageSearch :contact="contact" v-if="tab === 'message-search'" />
                <ContactProperties :contact="contact" v-if="tab === 'contact-properties'" />
                <ContactNotes v-if="tab === 'notes'" :visitor-id="contact._id" />
                <ReadyContents v-if="tab === 'ready-contents'" :contact="contact" />
                <AgentList v-if="tab === 'agents'" :contact="contact" />
                <ZohoWidget v-if="tab === 'zoho'" :contact="contact" />
                <VisitorMediaAndDocuments v-if="tab === 'media-and-document'" :visitorId="contact._id" />
                <FlowluWidget v-if="tab === 'flowlu' && enabledFlowluSites.includes(site._id)" :contact="contact" />
                <!-- WebTab Widget'ları -->
                <WebTabWidgetContent 
                    v-if="tab.startsWith('webtab-')" 
                    :webTab="getWebTabById(tab.replace('webtab-', ''))"
                    :contact="contact"
                />
            </div>
            <div class="user-info-menu">
                <div
                    class="user-info-menu-item gray"
                    :class="{ selected: tab === 'user-info' }"
                    @click="changeTab('user-info')"
                    v-tippy="{ followCursor: true, placement: 'right-end' }"
                    :content="$t('tour.popoverInfo')"
                >
                    <UserInfoSvg />
                </div>

                <div
                    class="user-info-menu-item gray"
                    :class="{ selected: tab === 'contact-properties' }"
                    @click="changeTab('contact-properties')"
                    v-tippy="{ followCursor: true, placement: 'right-end' }"
                    :content="$t('message.contactProperties')"
                >
                    <span class="material-icons hash-icon" style="font-size: 30px">tag</span>
                </div>

                <div
                    class="user-info-menu-item gray"
                    :class="{ selected: tab === 'notes' }"
                    @click="changeTab('notes')"
                    v-tippy="{ followCursor: true, placement: 'right-end' }"
                    :content="$t('message.notes')"
                >
                    <div class="user-info-menu-item-badge" v-if="contact.noteCount">
                        {{ contact.noteCount || 0 }}
                    </div>
                    <NotesSvg />
                </div>
                <div
                    class="user-info-menu-item gray"
                    :class="{ selected: tab === 'agents' }"
                    @click="changeTab('agents')"
                    v-tippy="{ followCursor: true, placement: 'right-end' }"
                    :content="$t('message.agentDetails')"
                >
                    <AgentsSvg />
                </div>
                <div
                    v-if="!isWatching()"
                    class="user-info-menu-item gray"
                    :class="{ selected: tab === 'ready-contents' }"
                    @click="changeTab('ready-contents')"
                    v-tippy="{ followCursor: true, placement: 'right-end' }"
                    :content="$t('plans.readyContents')"
                >
                    <ReadyContentsSvg />
                </div>
                <div
                    v-if="!isWatching()"
                    class="user-info-menu-item gray"
                    :class="{ selected: tab === 'media-and-document' }"
                    @click="changeTab('media-and-document')"
                    v-tippy="{ followCursor: true, placement: 'right-end' }"
                    :content="$t('message.sumChat_MediaFile')"
                >
                    <MediaAndDocumentSVG />
                </div>
                <div
                    class="user-info-menu-item gray"
                    :class="{ selected: tab === 'message-search' }"
                    @click="changeTab('message-search')"
                    v-tippy="{ followCursor: true, placement: 'right-end' }"
                    :content="$t('userInfoWidget.messageSearch')"
                >
                    <span class="material-icons hash-icon" style="font-size: 30px">search</span>
                </div>
                <div
                    class="user-info-menu-item gray"
                    :class="{ selected: tab === 'meta-referrals' }"
                    @click="changeTab('meta-referrals')"
                    v-tippy="{ followCursor: true, placement: 'right-end' }"
                    :content="'Meta Referrals'"
                >
                    <MetaReferralsSVG />
                </div>
                <div
                    v-if="zohoChannelExists"
                    class="user-info-menu-item zoho"
                    :class="{ selected: tab === 'zoho' }"
                    @click="changeTab('zoho')"
                    v-tippy="{ followCursor: true, placement: 'right-end' }"
                    :content="$t('userInfoWidget.zohoInfo')"
                >
                    <ZohoIcon />
                </div>
                <div
                    v-if="enabledFlowluSites.includes(site._id)"
                    class="user-info-menu-item flowlu"
                    :class="{ selected: tab === 'flowlu' }"
                    @click="changeTab('flowlu')"
                    v-tippy="{ followCursor: true, placement: 'right-end' }"
                    content="Flowlu"
                >
                    <FlowluLogo />
                </div>
                <!-- WebTab Widget'ları -->
                <div
                    v-for="webTab in contactWidgetWebTabs"
                    :key="'webtab-' + webTab._id"
                    class="user-info-menu-item webtab-item"
                    :class="{ selected: tab === 'webtab-' + webTab._id }"
                    @click="changeTab('webtab-' + webTab._id)"
                    v-tippy="{ followCursor: true, placement: 'right-end' }"
                    :content="webTab.description || webTab.name"
                >
                    <span 
                        class="material-icons webtab-icon" 
                        :style="{ color: webTab.iconColor || '#8796af' }"
                    >
                        {{ webTab.icon }}
                    </span>
                </div>
                <!--
                <div
                    v-if="!isWatching()"
                    class="user-info-menu-item gray"
                    :class="{ selected: tab === 'message-search' }"
                    @click="changeTab('message-search')"
                    content="Message Search"
                    v-tippy="{ followCursor: true, placement: 'right-end' }"
                    :content="$t('message.prevChats')"
                >
                    <ConversationHistorySvg />
                </div>
                -->
            </div>
        </div>
    </ResizableContent>
</template>
<script>
import ResizableContent from "./ResizableContent.vue";
import NotesSvg from "./svg/NotesSvg.vue";
import UserInfoSvg from "./svg/UserInfoSvg.vue";
import AgentsSvg from "./svg/AgentsSvg.vue";
import ReadyContentsSvg from "./svg/ReadyContentsSvg.vue";
import ConversationHistorySvg from "./svg/ConversationHistorySvg.vue";
import { mapState, mapGetters } from "vuex";
import ContactNotes from "./ContactNotes.vue";
import ReadyContents from "./ReadyContents.vue";
import ContactDetail from "./ContactDetail.vue";
import AgentList from "./AgentList.vue";
import MessageSearch from "./MessageSearch.vue";
import ContactProperties from "./ContactProperties.vue";
import MetaReferralsSVG from "./svg/MetaReferralsSVG.vue";
import MetaReferrals from "./MetaReferrals.vue";
import ZohoIcon from "../../../views/Pages/Chatbot/pages/integrations/svg/ZohoIcon.vue";
import ZohoWidget from "./zoho/ZohoWidget.vue";
import VisitorMediaAndDocuments from "./VisitorMediaAndDocuments.vue";
import MediaAndDocumentSVG from "./svg/MediaAndDocumentSVG.vue";
import FlowluWidget from "./flowlu/FlowluWidget.vue";
import FlowluLogo from "./flowlu/svg/FlowluLogo.vue";
import WebTabWidgetContent from "./WebTabWidgetContent.vue";

export default {
    name: "UserInfoWidget",
    components: {
        FlowluLogo,
        FlowluWidget,
        ZohoWidget,
        ZohoIcon,
        MetaReferrals,
        MetaReferralsSVG,
        ContactProperties,
        MessageSearch,
        AgentList,
        ContactDetail,
        ReadyContents,
        ContactNotes,
        ConversationHistorySvg,
        ReadyContentsSvg,
        AgentsSvg,
        UserInfoSvg,
        NotesSvg,
        ResizableContent,
        VisitorMediaAndDocuments,
        MediaAndDocumentSVG,
        WebTabWidgetContent,
    },
    props: {
        value: {
            type: Object,
            default: () => ({}),
        },
        contact: {
            type: Object,
            default: () => ({}),
        },
        maxUserInfoWidgetWidth: {
            type: [String, Number],
            default: null,
        },
        minUserInfoWidgetWidth: {
            type: [String, Number],
            default: null,
        },
        disabledResize: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        tab: "user-info",
        minimized: false,
        cloneWidth: 50,
        enabledFlowluSites: ["5d14ee322d95ad001ebdf213"],
    }),
    computed: {
        ...mapState({
            site: (state) => state.site.site,
            users: (state) => state.user.users.data,
            watchingContactIds: (state) => state.chat.watchingContactIds,
            zohoChannelExists: (state) => !!state.channel.channels.find((x) => x.type === "zoho"),
        }),
        ...mapGetters({
            contactWidgetWebTabs: "webtabs/getContactWidgetWebTabs",
        }),
    },
    methods: {
        getTabTitle() {
            if (this.tab === "user-info") return this.$t("userInfoWidget.userInformation");
            if (this.tab === "message-search") return this.$t("userInfoWidget.messageSearch");
            if (this.tab === "contact-properties") return this.$t("message.contactProperties");
            if (this.tab === "meta-referrals") return "Meta Referrals";
            if (this.tab === "notes") return this.$t("message.notes");
            if (this.tab === "agents") return this.$t("message.agentDetails");
            if (this.tab === "ready-contents") return this.$t("plans.readyContents");
            if (this.tab === "media-and-document") return this.$t("message.sumChat_MediaFile");
            if (this.tab === "zoho") return this.$t("userInfoWidget.zohoInfo");
            if (this.tab === "flowlu") return "Flowlu";
            // WebTab için başlık
            if (this.tab.startsWith("webtab-")) {
                const webTab = this.getWebTabById(this.tab.replace("webtab-", ""));
                return webTab ? webTab.name : "Web Sekmesi";
            }
        },
        getWebTabById(id) {
            return this.contactWidgetWebTabs.find((tab) => tab._id === id);
        },
        changeTab(tab) {
            if (this.minimized) this.maximize();
            this.tab = tab;
        },
        isWatching() {
            return this.watchingContactIds.includes(this.contact._id);
        },
        minimize() {
            this.minimized = true;
            this.cloneWidth = parseInt(`${this.minUserInfoWidgetWidth}`);
            this.$emit("width", this.cloneWidth);
        },
        maximize() {
            this.minimized = false;
            this.cloneWidth = parseInt(`${this.maxUserInfoWidgetWidth * 0.8}`);
            this.$emit("width", this.cloneWidth);
        },
        onResize(handle, x, y, width, height) {
            if (width !== this.cloneWidth) {
                this.cloneWidth = width;
            }
            this.minimized = width <= 80;
            this.$emit("width", width);
        },
    },
    beforeMount() {
        if(!this.disabledResize){
            const width = localStorage.getItem("user-info-widget-width") || `${this.maxUserInfoWidgetWidth * 0.7}`;
            this.cloneWidth = parseInt(width);
            this.minimized = this.cloneWidth <= 80;
            this.$emit("width", this.cloneWidth);
        }
    },
    watch: {
        cloneWidth() {
            localStorage.setItem("user-info-widget-width", this.cloneWidth);
        },
    },
};
</script>
<style lang="scss">
.user-info-widget {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    border-left: 1px solid var(--border-neutral-default);
    position: relative;

    .collapse-widget {
        width: 30px;
        height: 30px;
        background: var(--color-bg-neutral-default);
        border: 1px solid var(--border-neutral-default);
        border-radius: 40px;
        position: absolute;
        top: 50%;
        left: -15px;
        display: none;
        align-items: center;
        justify-content: center;
        user-select: none;
        cursor: pointer;
    }

    &.minimized {
        .user-info-content,
        .user-info-details,
        .user-info-content {
            display: none !important;
        }

        .user-info-menu {
            width: 100% !important;
        }

        .user-info-head {
            justify-content: center;
        }

        .collapse-widget {
            display: flex;
        }
    }

    .user-info-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        border-bottom: 1px solid var(--border-neutral-default);
        padding: 10px;
        font-weight: bold;
        font-size: 16px;
        user-select: none;
        color: #22272d;
        min-height: 50px;

        .material-icons {
            font-size: 18px;
        }
    }

    .user-info-wrapper {
        display: flex;
        width: 100%;
        height: 100%;
        overflow: hidden;

        .user-info-content {
            width: 100%;
            height: 100%;
            border-right: 1px solid var(--border-neutral-default);
            overflow: hidden;
            overflow-y: auto;

            .user-info-details {
                padding: 15px;
            }

            &::-webkit-scrollbar {
                display: none;
            }

            &-title {
                font-weight: bold;
                font-size: 16px;
                display: flex;
                align-items: center;

                &.sp-between {
                    justify-content: space-between;
                }

                &-actions {
                    display: flex;
                    align-items: center;
                    gap: 4px;

                    &-button {
                        width: 30px;
                        height: 30px;
                        cursor: pointer;
                        user-select: none;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        line-height: 0;

                        .material-icons {
                            font-size: 18px;
                        }
                    }
                }
            }
        }

        .user-info-menu {
            min-width: 50px;
            width: 50px;
            height: 100%;
            display: flex;
            align-items: center;
            flex-direction: column;
            gap: 4px;
            padding: 10px;

            &::-webkit-scrollbar {
                display: none;
            }

            .user-info-menu-item {
                padding: 5px;
                line-height: 0;
                border-radius: 2px;
                position: relative;

                &-badge {
                    position: absolute;
                    top: 0;
                    right: 0;
                    background: #ff4d4f;
                    color: white;
                    font-size: 10px;
                    width: 15px;
                    height: 15px;
                    border-radius: 15px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                &:hover,
                &.selected {
                    .hash-icon {
                        color: #5625cc;
                    }
                }

                .hash-icon {
                    cursor: default;
                    color: #8796af;
                }

                &.gray {
                    svg {
                        width: 25px;
                        height: 25px;

                        path {
                            //todo: color selector
                            fill: #8796af;
                        }
                    }
                }

                &.zoho {
                    svg {
                        width: 45px;
                    }
                }

                &.webtab-item {
                    .webtab-icon {
                        font-size: 25px;
                        cursor: default;
                    }

                    &:hover,
                    &.selected {
                        .webtab-icon {
                            filter: brightness(0.8);
                        }
                    }
                }

                &:hover,
                &.selected {
                    &.gray {
                        svg {
                            width: 25px;
                            height: 25px;

                            path {
                                //todo: color selector
                                fill: #5625cc;
                            }
                        }
                    }
                }
            }
        }
    }
}
</style>
